import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Target,
  Users,
  DollarSign,
  CheckCircle,
  AlertTriangle,
  Activity,
  Brain,
  Zap,
  Briefcase,
} from 'lucide-react'
import {
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import { projectsService, Project } from '../services/projects'
import { formatBudgetVariance } from '../utils/apiTransformers'
import { toast } from 'sonner'
import { useLocalization } from '../contexts/LocalizationContext'
import type { ApiErrorLike } from '../utils/apiError'

export default function ProjectDashboard() {
  const navigate = useNavigate()
  const { formatCurrency } = useLocalization()
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchProjects()
  }, [])

  const fetchProjects = async () => {
    setLoading(true)
    try {
      const data = await projectsService.list()
      setProjects(data)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error fetching projects:', error)
      toast.error('Failed to load projects')
    } finally {
      setLoading(false)
    }
  }

  // Calculate metrics from projects
  const activeProjects = projects.filter((p) => p.status === 'active').length
  const totalBudget = projects.reduce((sum, p) => sum + (parseFloat(p.budget || '0')), 0)
  const onTrackProjects = projects.filter((p) => p.status === 'active' && p.progress >= 70).length
  const onTrackPercentage = projects.length > 0 ? Math.round((onTrackProjects / projects.length) * 100) : 0

  const projectMetrics = [
    {
      label: 'Active Projects',
      value: activeProjects.toString(),
      change: `+${projects.filter((p) => p.status === 'active').length}`,
      icon: Target,
    },
    {
      label: 'Total Budget',
      value: formatCurrency(totalBudget, undefined),
      change: '+5.2%',
      icon: DollarSign,
    },
    {
      label: 'On Track',
      value: `${onTrackPercentage}%`,
      change: '+8%',
      icon: CheckCircle,
    },
    {
      label: 'Total Projects',
      value: projects.length.toString(),
      change: `+${projects.length}`,
      icon: Users,
    },
  ]

  // Project Status Distribution
  const statusData = [
    {
      name: 'On Track',
      value: projects.filter((p) => p.status === 'active' && p.progress >= 70).length,
      color: '#10b981',
    },
    {
      name: 'At Risk',
      value: projects.filter((p) => p.status === 'active' && p.progress < 70 && p.progress >= 50).length,
      color: '#f59e0b',
    },
    {
      name: 'Critical',
      value: projects.filter((p) => p.status === 'active' && p.progress < 50).length,
      color: '#ef4444',
    },
  ]

  // Project Status Distribution by Status Field
  const statusDistribution = [
    { name: 'Active', value: projects.filter((p) => p.status === 'active').length, color: '#10b981' },
    { name: 'Planning', value: projects.filter((p) => p.status === 'planning').length, color: '#3b82f6' },
    { name: 'On Hold', value: projects.filter((p) => p.status === 'on_hold').length, color: '#f59e0b' },
    { name: 'Completed', value: projects.filter((p) => p.status === 'completed').length, color: '#6b7280' },
    { name: 'Cancelled', value: projects.filter((p) => p.status === 'cancelled').length, color: '#ef4444' },
  ]

  // Critical Projects (low progress or over budget)
  const criticalProjects = projects
    .filter((p) => {
      if (p.status !== 'active') return false
      const progress = p.progress || 0
      const budget = parseFloat(p.budget || '0')
      const actualCost = parseFloat(p.actual_cost || '0')
      return progress < 50 || (budget > 0 && actualCost > budget * 1.1)
    })
    .slice(0, 3)
    .map((p) => {
      const budget = parseFloat(p.budget || '0')
      const actualCost = parseFloat(p.actual_cost || '0')
      return {
        id: p.id,
        name: p.name,
        health: p.progress || 0,
        budget,
        spent: actualCost,
        // KQA-9 — "—" when no budget or no spend yet, never a misleading -100%.
        variance: formatBudgetVariance(p.budget, p.actual_cost),
        progress: p.progress || 0,
      }
    })

  // Top Performing Projects
  const topProjects = projects
    .filter((p) => p.status === 'active' && (p.progress || 0) >= 70)
    .sort((a, b) => (b.progress || 0) - (a.progress || 0))
    .slice(0, 3)
    .map((p) => {
      const budget = parseFloat(p.budget || '0')
      const actualCost = parseFloat(p.actual_cost || '0')
      return {
        id: p.id,
        name: p.name,
        health: p.progress || 0,
        completion: p.progress || 0,
        budget,
        spent: actualCost,
        // KQA-9 — "—" when no budget or no spend yet, never a misleading -100%.
        variance: formatBudgetVariance(p.budget, p.actual_cost),
      }
    })

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#486581] mx-auto mb-4"></div>
          <p className="text-[#486581]">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6 bg-[#f0f4f8] min-h-screen">
      <div className="max-w-[1600px] mx-auto space-y-6">
        {/* Header */}
        <div className="mb-6">
          <div className="mb-4">
            <h1 className="text-3xl font-semibold text-[#2A3B4C] mb-2">Project Dashboard</h1>
            <p className="text-lg text-[#556B7C]">Real-time insights and performance tracking across all active projects</p>
          </div>
          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4">
            {projectMetrics.map((m, index) => {
              const Icon = m.icon
              return (
                <div key={index} className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
                  <div className="flex items-start justify-between mb-2">
                    <div className="text-sm text-[#6F8394]">{m.label}</div>
                    <div className="p-2 rounded-lg bg-blue-50">
                      <Icon className="w-4 h-4 text-blue-600" />
                    </div>
                  </div>
                  <div className="text-2xl font-semibold text-[#2A3B4C]">{m.value}</div>
                  {m.change && <div className="text-xs text-[#6F8394] mt-1">{m.change}</div>}
                </div>
              )
            })}
          </div>
        </div>

        {/* AI Insights Banner */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Brain className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2">AI Project Intelligence</h3>
              <p className="text-indigo-100 mb-3">
                {criticalProjects.length > 0
                  ? `${criticalProjects.length} project${criticalProjects.length > 1 ? 's are' : ' is'} at risk of budget overrun or schedule delay. Review critical projects for immediate action.`
                  : 'All projects are performing well. Continue monitoring progress and budget utilization.'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/project')}
                  className="px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors text-sm"
                >
                  View All Projects
                </button>
                <button
                  onClick={() => navigate('/project/create')}
                  className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors text-sm"
                >
                  Create New Project
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Status & Stage Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Project Status Distribution */}
          <div className="bg-white rounded-lg border-2 border-[#bcccdc] shadow-md p-6">
            <h3 className="text-lg font-semibold text-[#102a43] mb-4">Project Status Distribution</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={statusDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 grid grid-cols-3 gap-3">
              {statusData.map((item) => (
                <div key={item.name} className="text-center p-3 bg-[#f0f4f8] rounded-lg">
                  <div className="text-2xl font-bold" style={{ color: item.color }}>
                    {item.value}
                  </div>
                  <div className="text-xs text-[#486581] mt-1">{item.name}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Project Health Distribution */}
          <div className="bg-white rounded-lg border-2 border-[#bcccdc] shadow-md p-6">
            <h3 className="text-lg font-semibold text-[#102a43] mb-4">Project Health Distribution</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              {statusData.map((item) => (
                <div key={item.name} className="flex items-center gap-2 text-sm">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-[#486581]">{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Critical Projects & Top Performers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Critical Projects */}
          <div className="bg-white rounded-lg border-2 border-[#bcccdc] shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#102a43]">Critical Projects</h3>
              {criticalProjects.length > 0 && (
                <span className="px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                  {criticalProjects.length} Critical
                </span>
              )}
            </div>
            {criticalProjects.length === 0 ? (
              <div className="text-center py-8 text-[#829ab1]">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>No critical projects at this time</p>
              </div>
            ) : (
              <div className="space-y-4">
                {criticalProjects.map((project) => (
                  <div
                    key={project.id}
                    className="p-4 border-2 border-red-200 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                    onClick={() => navigate(`/project/${project.id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-[#102a43]">{project.name}</h4>
                        <div className="text-sm text-[#486581] mt-1">Progress: {project.progress}%</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-[#829ab1]">Health</div>
                        <div className="text-xl font-bold text-red-600">{project.health}%</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 pt-3 border-t border-red-200">
                      <div>
                        <div className="text-xs text-[#829ab1]">Variance</div>
                        <div className="text-sm font-medium text-red-700">{project.variance}</div>
                      </div>
                      <div>
                        <div className="text-xs text-[#829ab1]">Budget</div>
                        <div className="text-sm font-medium text-[#486581]">
                          {formatCurrency(project.budget, undefined)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-[#829ab1]">Spent</div>
                        <div className="text-sm font-medium text-red-700">
                          ${(project.spent / 1000000).toFixed(1)}M
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top Performing Projects */}
          <div className="bg-white rounded-lg border-2 border-[#bcccdc] shadow-md p-6">
            <h3 className="text-lg font-semibold text-[#102a43] mb-4">Top Performing Projects</h3>
            {topProjects.length === 0 ? (
              <div className="text-center py-8 text-[#829ab1]">
                <Briefcase className="w-12 h-12 mx-auto mb-2 text-[#829ab1]" />
                <p>No active projects yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {topProjects.map((project) => (
                  <div
                    key={project.id}
                    className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 cursor-pointer hover:bg-green-100 transition-colors"
                    onClick={() => navigate(`/project/${project.id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-[#102a43]">{project.name}</h4>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-[#829ab1]">Health</div>
                        <div className="text-xl font-bold text-green-600">{project.health}%</div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="flex justify-between text-xs text-[#829ab1] mb-1">
                        <span>Completion</span>
                        <span>{project.completion}%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-green-600 rounded-full"
                          style={{ width: `${project.completion}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-green-200">
                      <div>
                        <div className="text-xs text-[#829ab1]">Budget Variance</div>
                        <div className="text-sm font-medium text-green-700">{project.variance}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-[#829ab1]">Spent / Budget</div>
                        <div className="text-sm font-medium text-[#102a43]">
                          ${(project.spent / 1000000).toFixed(1)}M / ${(project.budget / 1000000).toFixed(1)}M
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg border-2 border-[#bcccdc] shadow-md p-6">
          <h3 className="text-lg font-semibold text-[#102a43] mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/project')}
              className="p-4 border-2 border-[#bcccdc] rounded-lg hover:border-[#486581] hover:bg-[#f0f4f8] transition-colors text-left group"
            >
              <Target className="w-6 h-6 text-[#829ab1] group-hover:text-[#486581] mb-2" />
              <div className="font-medium text-[#102a43] text-sm">View All Projects</div>
            </button>
            <button
              onClick={() => navigate('/project/create')}
              className="p-4 border-2 border-[#bcccdc] rounded-lg hover:border-[#486581] hover:bg-[#f0f4f8] transition-colors text-left group"
            >
              <Zap className="w-6 h-6 text-[#829ab1] group-hover:text-[#486581] mb-2" />
              <div className="font-medium text-[#102a43] text-sm">Create New Project</div>
            </button>
            <button
              onClick={() => navigate('/project')}
              className="p-4 border-2 border-[#bcccdc] rounded-lg hover:border-[#486581] hover:bg-[#f0f4f8] transition-colors text-left group"
            >
              <AlertTriangle className="w-6 h-6 text-[#829ab1] group-hover:text-[#486581] mb-2" />
              <div className="font-medium text-[#102a43] text-sm">Risk Dashboard</div>
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="p-4 border-2 border-[#bcccdc] rounded-lg hover:border-[#486581] hover:bg-[#f0f4f8] transition-colors text-left group"
            >
              <Activity className="w-6 h-6 text-[#829ab1] group-hover:text-[#486581] mb-2" />
              <div className="font-medium text-[#102a43] text-sm">Performance Reports</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

